import React, { useEffect } from "react";

/**
 * Props for the tooltip component used in the TimeLineChart.
 *
 * @property title - The main title displayed at the top of the tooltip.
 * @property yLabel - The label describing the Y-axis value (e.g., "Latency", "Value").
 * @property timeLabel - The label describing the time value (e.g., "Timestamp", "Time").
 * @property yValueUnit - (Optional) The unit for the Y-axis value (e.g., "ms", "%", "requests").
 * @property timeUnit - The unit for the time value, either milliseconds ("ms") or seconds ("s").
 *
 * Use this interface to provide all necessary information for rendering a contextual tooltip
 * in the TimeLineChart, ensuring users understand the data point being hovered.
 */
export interface TimeChartTooltipProps {
  title?: string;
  yLabel?: string;
  timeLabel?: string;
  yValueUnit?: string;
  timeUnit: "ms" | "s";
}

export interface TimeChartTooltipDataProps {
  yValue: string | number;
  timeStamp: number;
  focusedTimestamp: (timeStamp: number) => void;
  defocus: () => void;
}

type Props = TimeChartTooltipProps & TimeChartTooltipDataProps;

const TimeChartTooltip: React.FC<Props> = ({
  title = "",
  yLabel = "value",
  yValue,
  yValueUnit = "",
  timeLabel = "time",
  timeStamp,
  timeUnit,
  focusedTimestamp,
  defocus,
}) => {
  useEffect(() => {
    focusedTimestamp(timeStamp);

    return () => {
      defocus();
    };
  }, [timeStamp]);

  /**
  timeLabel: "time"
  timeStamp: undefined
  timeUnit: "ms"
  title: "Eye Distance"
  yLabel: "distance"
  yValue: 2.24
 */

  return (
    <div
      className="custom-tooltip"
      style={{
        padding: "10px",
        border: "1px solid var(--oculera-border)",
        backgroundColor: "var(--oculera-surface)",
        borderRadius: "4px",
      }}
    >
      {title && (
        <div className="tooltip-title" style={{ fontWeight: "bold", marginBottom: "5px" }}>
          {title}
        </div>
      )}
      {
        <div className="tooltip-x">
          <span style={{ fontWeight: "bold" }}>{timeLabel}: </span>
          <span>{timeStamp + timeUnit}</span>
        </div>
      }
      {yLabel && (
        <div className="tooltip-y">
          <span style={{ fontWeight: "bold" }}>{yLabel}: </span>
          <span>{yValue + yValueUnit}</span>
        </div>
      )}
    </div>
  );
};

export default TimeChartTooltip;
